<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';

import { i18n } from '@apps/main/modules/localization/localizationSetup';

import type { AppSingleSelectOption } from '@libs/ui';
import { TransportType } from '@libs/trip-planner-interfaces';
import { AppSingleSelect } from '@libs/ui';

interface TransportSelectorProps {
  transportType: TransportType;
  initialOption: string;
  disabled: boolean;
  isUpsellNoTransport?: boolean;
  hasUserSelectedTransport?: boolean;
  isTransportUpsellBanner?: boolean;
}

const props = defineProps<TransportSelectorProps>();

interface TransportSelectorEmits {
  (eventName: 'select', value: TransportType): void;
  (eventName: 'open'): void;
}

const emit = defineEmits<TransportSelectorEmits>();

const { t } = i18n.global;

const selectedOption = ref<AppSingleSelectOption<TransportType> | null>(null);

const updateableSelectedOption = [
  TransportType.Bicycle,
  TransportType.Walk,
  TransportType.Car,
];

const editFlightLabel = `${t('planner.transport.flight')} - ${t(
  'planner.edit'
)}`;
const editFerryLabel = `${t('planner.transport.ferry')} - ${t('planner.edit')}`;

const options: AppSingleSelectOption<TransportType>[] = [
  {
    id: TransportType.None,
    label: `${t('planner.transport.no_transport_selected')}`,
    icon: 'noTransport',
  },
  {
    id: TransportType.Flight,
    label: `${t('planner.transport.flight')}`,
    icon: 'airplane',
  },
  {
    id: TransportType.Ferry,
    label: `${t('planner.transport.ferry')}`,
    icon: 'ferry',
  },
  {
    id: TransportType.Car,
    label: `${t('planner.transport.car')}`,
    icon: 'car',
  },
  {
    id: TransportType.Walk,
    label: `${t('planner.transport.walking')}`,
    icon: 'personWalking',
  },
  {
    id: TransportType.Bicycle,
    label: `${t('planner.transport.bicycle')}`,
    icon: 'personBicycling',
  },
];

const shouldDisplayImprovedStyle = computed(
  () => !props.isUpsellNoTransport || props.hasUserSelectedTransport
);

function onSelect(option: AppSingleSelectOption<TransportType>): void {
  // Update selected option only for simple transport types, for others keep the previous value as itinerary segment will be replaced with new one
  if (updateableSelectedOption.includes(option.id)) {
    selectedOption.value = option;
  } else {
    const deepCopy = JSON.parse(JSON.stringify(selectedOption.value));
    selectedOption.value = null;
    selectedOption.value = deepCopy;
  }
  emit('select', option.id);
}

onMounted(() => {
  const selectedInitialOption = options.find(
    (option) => option.id === props.transportType
  );

  if (selectedInitialOption) {
    if (props.transportType === TransportType.None) {
      const selectedInitialOptionOnDisplay = { ...selectedInitialOption };
      selectedInitialOptionOnDisplay.label = props.initialOption;
      selectedOption.value = selectedInitialOptionOnDisplay;
    } else {
      const selectedInitialOptionOnDisplay = { ...selectedInitialOption };
      if (props.transportType === TransportType.Flight) {
        selectedInitialOption.label = editFlightLabel;
      } else if (props.transportType === TransportType.Ferry) {
        selectedInitialOption.label = editFerryLabel;
      }
      selectedOption.value = selectedInitialOptionOnDisplay;
    }
  }
});
</script>

<template>
  <div
    class="transport-selector"
    :class="{
      underlined: props.isUpsellNoTransport || !props.hasUserSelectedTransport,
      'transport-upsell-banner': props.isTransportUpsellBanner,
    }"
  >
    <AppSingleSelect
      class="transport-select"
      :class="shouldDisplayImprovedStyle ? 'primary' : 'transparent'"
      :type="shouldDisplayImprovedStyle ? 'primary' : 'transparent'"
      :options="options"
      :selected="selectedOption"
      :disabled="props.disabled"
      :size="shouldDisplayImprovedStyle ? undefined : 'size-s'"
      :left-side-icon="
        shouldDisplayImprovedStyle
          ? selectedOption?.icon ?? undefined
          : undefined
      "
      left-side-icon-size="18"
      @select="onSelect"
      @open="emit('open')"
    />
  </div>
</template>

<style scoped lang="scss">
.transport-selector {
  display: flex;
  &.underlined {
    text-decoration: underline;
  }
  &.transport-upsell-banner {
    height: 100%;
    text-decoration: none;
    .transport-select {
      height: 100%;
      padding: 0 var(--spacing-8);
      border: 1px solid var(--color-neutral-70);
      border-radius: var(--border-radius-8);
      @include font-button-small;
      background: var(--color-white);
      @include for-less-or-equal-than-599-px {
        @include font-label-s-light;
        padding: 0;
        padding-left: var(--spacing-8);
        border: none;
        text-decoration: underline;
        background: none;
      }
    }
  }
}
</style>
